import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Radio, Zap, TrendingUp, Shield, CheckCircle, ArrowRight, MessageSquare, Eye, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

const API = import.meta.env.VITE_API_URL || "";
const TENANT_ID = import.meta.env.VITE_TENANT_ID || null;

const TIERS = [
  {
    key: "signal",
    name: "Signal",
    price: 79,
    tagline: "Available now",
    features: [
      "40 AI-drafted replies / month",
      "Reddit monitoring (15+ subreddits)",
      "Pain-signal scoring (0–100)",
      "Human approval workflow",
      "1 industry focus",
      "Expert replies powered by 348+ tool database",
      "One-click approve → auto-posts to Reddit",
      "Hot lead email alerts (pain score 75+)",
      "Live dashboard with weekly metrics",
      "Trending industry pain point feed",
    ],
    cta: "Start with Signal — $79/mo",
    featured: true,
    live: true,
  },
  {
    key: "outreach",
    name: "Outreach",
    price: 197,
    tagline: "Coming soon",
    features: [
      "160 AI-drafted replies / month",
      "Reddit + Bluesky monitoring",
      "Pain-signal scoring (0–100)",
      "Draft quality auto-grading",
      "Author momentum tracking",
      "3 industry verticals",
    ],
    cta: "Join Waitlist",
    featured: false,
    live: false,
  },
  {
    key: "intelligence",
    name: "Intelligence",
    price: 397,
    tagline: "Coming soon",
    features: [
      "Unlimited AI-drafted replies",
      "Reddit + Bluesky + Google Reviews",
      "Pain-signal scoring (0–100)",
      "Draft quality auto-grading",
      "Author momentum tracking",
      "Competitor monitoring",
      "Lead magnet delivery",
      "Unlimited industries",
    ],
    cta: "Join Waitlist",
    featured: false,
    live: false,
  },
];

const HOW_IT_WORKS = [
  {
    icon: Eye,
    title: "Monitor",
    desc: "Our AI scans Reddit, Bluesky, and Google Reviews 24/7 for business owners asking for help in your niche.",
  },
  {
    icon: MessageSquare,
    title: "Draft",
    desc: "For every high-signal post, we generate a personalized expert reply using our proprietary 400+ AI tool database.",
  },
  {
    icon: ThumbsUp,
    title: "Convert",
    desc: "You review and approve each reply with one click. Leads come to you — no cold outreach, no spam.",
  },
];

export default function SignalInboxPage() {
  const [loadingTier, setLoadingTier] = useState(null);
  const [emails, setEmails] = useState({ signal: "", outreach: "", intelligence: "" });
  const [usageData, setUsageData] = useState(null);

  // Fetch trial usage if tenant
  useEffect(() => {
    if (!TENANT_ID) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/tenant/${TENANT_ID}/usage`, {
          headers: { "X-Admin-Key": import.meta.env.VITE_ADMIN_KEY || "" },
        });
        if (res.ok) setUsageData(await res.json());
      } catch { /* silent */ }
    })();
  }, []);

  const handleCheckout = async (tier) => {
    const email = emails[tier];
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoadingTier(tier);
    try {
      const res = await fetch(`${API}/api/marketing/create-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier,
          email,
          tenant_id: TENANT_ID || "",
          origin_url: window.location.href,
        }),
      });
      const data = await res.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        toast.error("Checkout failed — please try again");
      }
    } catch {
      toast.error("Network error — please try again");
    } finally {
      setLoadingTier(null);
    }
  };

  const trialSub = usageData?.subscription;

  return (
    <div className="min-h-screen bg-[#0c1324]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-semibold tracking-wider uppercase mb-6">
            <Radio size={14} /> Signal Inbox
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] tracking-tight mb-6">
            Your ideal customers are{" "}
            <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
              asking for help
            </span>{" "}
            on Reddit right now
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed mb-8">
            We find them, draft expert replies using our proprietary 400+ AI tool database,
            and queue everything for your one-click approval. Leads come to you.
          </p>
          <a
            href="#pricing"
            className="inline-flex items-center gap-2 bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] transition-all"
          >
            See Pricing <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-14 tracking-tight">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((step, i) => (
              <div
                key={step.title}
                className="relative bg-slate-900/60 border border-slate-800/60 rounded-2xl p-8 text-center"
              >
                <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mx-auto mb-5">
                  <step.icon size={24} className="text-blue-400" />
                </div>
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-slate-400 leading-relaxed text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trial Banner */}
      {trialSub && trialSub.tier === "trial" && trialSub.days_remaining_in_trial != null && (
        <section className="py-6 px-4">
          <div className="max-w-3xl mx-auto bg-amber-500/10 border border-amber-500/20 rounded-2xl p-6 text-center">
            <p className="text-amber-300 font-semibold">
              Your free trial has{" "}
              <span className="text-amber-200 font-bold">{trialSub.days_remaining_in_trial} days</span>{" "}
              remaining — {trialSub.drafts_used_this_month}/{trialSub.drafts_limit} drafts used this month
            </p>
            <p className="text-amber-400/70 text-sm mt-1">
              Upgrade below to keep your leads flowing
            </p>
          </div>
        </section>
      )}

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 border-t border-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4 tracking-tight">
            Simple, transparent pricing
          </h2>
          <p className="text-slate-400 text-center mb-14 max-w-xl mx-auto">
            Every plan includes human-in-the-loop approval. Nothing posts without you.
          </p>

          <div className="grid md:grid-cols-3 gap-6 items-start">
            {TIERS.map((tier) => (
              <div
                key={tier.key}
                className={`relative rounded-2xl p-8 ${
                  tier.featured
                    ? "bg-gradient-to-b from-blue-600/10 to-slate-900/80 border-2 border-blue-500/30 shadow-xl shadow-blue-500/10"
                    : "bg-slate-900/60 border border-slate-800/60"
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full">
                    Most Popular
                  </div>
                )}
                <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1">
                  {tier.tagline}
                </p>
                <h3 className="text-2xl font-bold text-white mb-1">{tier.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-extrabold text-white">${tier.price}</span>
                  <span className="text-slate-500 text-sm">/mo</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <CheckCircle size={16} className="text-blue-400 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {tier.live ? (
                  <>
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={emails[tier.key]}
                      onChange={(e) => setEmails((prev) => ({ ...prev, [tier.key]: e.target.value }))}
                      className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 mb-3"
                    />
                    <button
                      onClick={() => handleCheckout(tier.key)}
                      disabled={loadingTier === tier.key}
                      className="w-full py-3.5 rounded-xl font-bold text-sm transition-all bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingTier === tier.key ? "Redirecting to checkout..." : tier.cta}
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="email"
                      placeholder="you@company.com — get notified at launch"
                      value={emails[tier.key]}
                      onChange={(e) => setEmails((prev) => ({ ...prev, [tier.key]: e.target.value }))}
                      className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 mb-3"
                    />
                    <button
                      onClick={async () => {
                        const em = emails[tier.key];
                        if (!em || !em.includes("@")) { toast.error("Enter your email to join the waitlist"); return; }
                        try {
                          await fetch(`${API}/api/subscribe`, {
                            method: "POST", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ email: em, source: `waitlist_${tier.key}` }),
                          });
                          toast.success(`You're on the ${tier.name} waitlist! We'll email you at launch.`);
                          setEmails((prev) => ({ ...prev, [tier.key]: "" }));
                        } catch { toast.error("Something went wrong — try again"); }
                      }}
                      className="w-full py-3.5 rounded-xl font-bold text-sm transition-all bg-slate-800 border border-slate-700/50 text-white hover:border-blue-500/30 hover:bg-slate-800/80"
                    >
                      {tier.cta}
                    </button>
                    <p className="text-[10px] text-slate-600 text-center mt-2">Launching soon — join the waitlist for early access + discount</p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 border-t border-slate-800/50">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/40 border border-slate-700/40 rounded-3xl p-10">
            <Shield size={32} className="text-blue-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-3">
              Not sure yet? Start with a $149 AI Blueprint
            </h2>
            <p className="text-slate-400 mb-6 max-w-lg mx-auto">
              Get a personalized AI implementation report for your business.
              Every report purchase includes a free 30-day Signal Inbox trial — see your first leads before you commit.
            </p>
            <a
              href="/audit"
              className="inline-flex items-center gap-2 bg-gradient-to-br from-blue-500 to-blue-600 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] transition-all"
            >
              Get Your AI Blueprint <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
